/**
 * docs/BILLING.md — packages, campaigns and USDT payments. Money is a decimal string, times are
 * ISO-8601 UTC, as everywhere in the API.
 */

import type { UUID } from '@/types/api';

/**
 * The switches a package has, in the built-in order: the scanner before the autopilot, as in the
 * menu (the autopilot runs on what the scanner finds). An administrator may set another order,
 * one for every package — the screens list them by `orderFeatures(feature_order)`.
 */
export const FEATURE_KEYS = [
  'scanner',
  'autopilot',
  'volatility',
  // docs/VOLATILITY.md §5 — top-N enter/leave alerts on the volatility board: the Admin package
  // or an administrator only (`useIncludes`), off by default and not opened by billing being off.
  'volatility_alerts',
  'volume',
  'signal_lab',
  'insights',
  'backtest',
  'web_sources',
  'copy_trade',
  'bot_defaults',
  'dca',
  'grid',
  'rebalance',
  'accumulate',
  'hedge_stop',
  'hedge',
  'multi_coin',
  // Moves real resting orders every minute: opened by the Admin package or an administrator only
  // (`useIncludes`), off by default and not opened by billing being switched off.
  'atr_live',
  // Nabız, the autopilot's third source: the Admin package only, like `atr_live`.
  'pulse',
  'aether_theme',
] as const;
export type FeatureKey = (typeof FEATURE_KEYS)[number];

/**
 * Bot types and the backtest every package had before they became switches (2026-10-15), and
 * several coins on one bot (2026-10-17, Trello #5): a plan saved earlier has no key for them and
 * counts as having them — as the backend reads it (`DEFAULT_ON`).
 */
const DEFAULT_ON: ReadonlySet<FeatureKey> = new Set<FeatureKey>([
  // docs/COPY_TRADE.md — open to every package since 2026-10-24 (Beta), unless one turns it off.
  'copy_trade',
  'dca',
  'rebalance',
  'accumulate',
  'backtest',
  'multi_coin',
]);

/**
 * The switches in the administrator's order (`feature_order`), any it does not name after them
 * in the built-in order — the backend's `feature_order`, so an older answer still lists them all.
 */
export function orderFeatures(order: readonly string[] | null | undefined): FeatureKey[] {
  const known = new Set<string>(FEATURE_KEYS);
  const named = [...new Set(order ?? [])].filter((key): key is FeatureKey => known.has(key));
  return [...named, ...FEATURE_KEYS.filter((key) => !named.includes(key))];
}

/** Whether `features` opens `key`, a missing key read the backend's way. */
export function hasFeature(
  features: Partial<Record<FeatureKey, boolean>> | undefined,
  key: FeatureKey,
): boolean {
  return features?.[key] ?? DEFAULT_ON.has(key);
}

/**
 * A package with the autopilot always has these too, and the volatility page's quick bot is a
 * DCA bot, so it brings DCA (the backend normalises it) — as do the volume page's bots.
 */
export const FEATURE_REQUIRES: Partial<Record<FeatureKey, FeatureKey[]>> = {
  autopilot: ['scanner', 'bot_defaults'],
  volatility: ['dca'],
  volume: ['dca'],
};

/** The package switch each bot strategy needs (`Bot.strategy` → feature). */
export const STRATEGY_FEATURE = {
  dca: 'dca',
  grid: 'grid',
  rebalance: 'rebalance',
  accumulate: 'accumulate',
  hedge_stop: 'hedge_stop',
} as const satisfies Record<string, FeatureKey>;

/** The counts a package caps; `null` = no cap. `max_daily_backtests` renews every midnight
 * (Türkiye) — `BillingMe.daily_resets_at`. `max_open_deals` is the user's deals open at once,
 * every bot of theirs counted; at it a bot simply opens nothing new (docs/BILLING.md §5.1). */
export const LIMIT_KEYS = [
  'max_exchange_accounts',
  'max_running_bots',
  'max_open_deals',
  'max_autopilot_bots',
  'max_web_sources',
  'max_daily_backtests',
] as const;
export type LimitKey = (typeof LIMIT_KEYS)[number];

export interface PlanPrice {
  months: number;
  /** Before any campaign. */
  list_price: string;
  /** After the best automatic campaign; equal to `list_price` when none applies. */
  price: string;
  campaign_name: string | null;
}

export interface Plan {
  id: UUID;
  name: string;
  description: string | null;
  badge: string | null;
  highlights: string[];
  prices: PlanPrice[];
  features: Record<FeatureKey, boolean>;
  limits: Record<LimitKey, number | null>;
  sort_order: number;
}

export interface CampaignBanner {
  id: UUID;
  name: string;
  description: string | null;
  /** A coupon to type at checkout; `null` = applied by itself. */
  code: string | null;
  kind: 'percent' | 'amount';
  value: string;
  plan_ids: string[] | null;
  months: number[] | null;
  ends_at: string | null;
}

/** `GET /billing/public/plans` — no sign-in (the landing page). */
export interface PublicPlans {
  plans: Plan[];
  banners: CampaignBanner[];
  /** The order every package lists its switches in (the administrator's). */
  feature_order?: FeatureKey[];
}

export interface Network {
  id: UUID;
  asset: string;
  network: string;
  label: string;
  note: string | null;
  memo_required: boolean;
}

/** `GET /billing/plans` — signed in: the per-user prices and the networks to pay on. */
export interface PlansResponse extends PublicPlans {
  enforced: boolean;
  networks: Network[];
}

export type EntitlementState = 'active' | 'trial' | 'grace' | 'default' | 'none';

export interface SubscriptionInfo {
  plan_id: UUID;
  plan_name: string;
  status: 'active' | 'trial';
  starts_at: string;
  ends_at: string | null;
  source: 'payment' | 'admin' | 'trial' | 'coupon';
}

/** `GET /billing/me` — what the signed-in user may use now. */
export interface BillingMe {
  enforced: boolean;
  /** An administrator: nothing is locked. */
  exempt: boolean;
  state: EntitlementState;
  plan: Plan | null;
  subscription: SubscriptionInfo | null;
  features: Record<FeatureKey, boolean>;
  limits: Record<LimitKey, number | null>;
  usage: Record<LimitKey, number>;
  grace_days: number;
  /** When today's daily allowances (`max_daily_backtests`) are full again. */
  daily_resets_at?: string;
  /** docs/BILLING.md §9.1 — the coupon the account's sign-up link carried, for the checkout. */
  landing_coupon?: string | null;
  /** §10 — "your trial / package ends in N days", once a reminder went out for this end. */
  reminder?: Reminder | null;
}

/** docs/BILLING.md §10 — the in-app notice before a trial or package ends. */
export interface Reminder {
  id: UUID;
  ends_at: string;
  days_before: number;
  plan_name: string;
  status: 'active' | 'trial';
  /** The offered coupon while its campaign still runs. */
  coupon_code: string | null;
  /** `20 %` / `5 USDT`. */
  coupon_discount: string | null;
}

export interface Quote {
  plan_id: UUID;
  months: number;
  list_price: string;
  discount: string;
  price: string;
  campaign_name: string | null;
  code_error: 'unknown' | 'expired' | 'not_for_this' | 'used_up' | 'already_used' | null;
}

export type InvoiceStatus = 'pending' | 'paid' | 'expired' | 'cancelled' | 'review';

/**
 * `plan` buys a package for `months`; `credit` tops up the Copy Trade credit (docs/COPY_TRADE.md
 * §4) — no plan (`plan_id` null), `months` 0, `price` the USDT that lands on the credit.
 */
export type InvoiceKind = 'plan' | 'credit';

export interface Invoice {
  id: UUID;
  kind: InvoiceKind;
  plan_id: UUID | null;
  plan_name: string;
  months: number;
  list_price: string;
  discount: string;
  campaign_name: string | null;
  price: string;
  tag: number;
  /** Exactly what to send: the price plus the tag in the third and fourth decimals. */
  amount_due: string;
  asset: string;
  network: string;
  network_label: string;
  address: string;
  memo: string | null;
  status: InvoiceStatus;
  created_at: string;
  expires_at: string;
  paid_at: string | null;
  paid_amount: string | null;
  tx_id: string | null;
  period_start: string | null;
  period_end: string | null;
  note: string | null;
}

/* ------------------------------------------------------------------------------------ */
/* Admin                                                                                  */
/* ------------------------------------------------------------------------------------ */

export interface AdminPlan extends Plan {
  is_public: boolean;
  subscribers: number;
  created_at: string;
}

export interface PlanWrite {
  name: string;
  description: string | null;
  badge: string | null;
  highlights: string[];
  prices: { months: number; price: string }[];
  features: Record<FeatureKey, boolean>;
  limits: Record<LimitKey, number | null>;
  is_public: boolean;
  sort_order: number;
  reason?: string;
}

export interface Campaign {
  id: UUID;
  name: string;
  description: string | null;
  code: string | null;
  kind: 'percent' | 'amount';
  value: string;
  plan_ids: string[] | null;
  months: number[] | null;
  starts_at: string | null;
  ends_at: string | null;
  max_uses: number | null;
  uses: number;
  per_user_limit: number | null;
  show_banner: boolean;
  is_active: boolean;
  /** Active, inside its dates and not used up — applying right now. */
  running: boolean;
  created_at: string;
}

export interface CampaignWrite {
  name: string;
  description: string | null;
  code: string | null;
  kind: 'percent' | 'amount';
  value: string;
  plan_ids: string[] | null;
  months: number[] | null;
  starts_at: string | null;
  ends_at: string | null;
  max_uses: number | null;
  per_user_limit: number | null;
  show_banner: boolean;
  is_active: boolean;
  reason?: string;
}

export interface PaymentAddress {
  id: UUID;
  asset: string;
  network: string;
  label: string;
  address: string;
  memo: string | null;
  note: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
}

export type PaymentAddressWrite = Omit<PaymentAddress, 'id' | 'created_at'> & { reason?: string };

export interface WatchStatus {
  configured: boolean;
  masked: string | null;
  last_run_at: string | null;
  last_ok_at: string | null;
  last_error: string | null;
  read: number;
}

/** `GET/PUT /admin/billing/feature-order` — one order for every package. */
export interface FeatureOrder {
  order: FeatureKey[];
}

export interface BillingSettings {
  enforced: boolean;
  default_plan_id: UUID | null;
  trial_plan_id: UUID | null;
  trial_days: number;
  invoice_minutes: number;
  grace_days: number;
  fee_tolerance: string;
  /** docs/BILLING.md §10 — days before an end that its user is reminded; empty = off. */
  reminder_days: number[];
  /** §10 — the coupon code the reminder offers; null = none. */
  reminder_coupon: string | null;
  binance: WatchStatus;
}

export type BillingSettingsWrite = Omit<BillingSettings, 'binance'> & { reason?: string };

export interface OwnerRef {
  id: UUID;
  email: string;
}

export interface AdminInvoice extends Invoice {
  owner: OwnerRef;
  match_method: 'amount' | 'tag' | 'covered' | 'txid' | 'manual' | 'free' | null;
  deposit_id: UUID | null;
}

export interface Deposit {
  id: UUID;
  tx_id: string | null;
  asset: string;
  network: string | null;
  amount: string;
  address: string | null;
  status: 'ok' | 'pending' | 'failed';
  received_at: string;
  invoice_id: UUID | null;
  matched_at: string | null;
  ignored: boolean;
  owner: OwnerRef | null;
}

export interface BillingSummary {
  revenue_total: string;
  revenue_30d: string;
  paid_30d: number;
  pending: number;
  review: number;
  unmatched_deposits: number;
  subscribers: { plan_id: UUID; name: string; active: number; trial: number }[];
}

/** docs/BILLING.md §9.2 — the tag of the sign-up link the counts are grouped by. */
export type SignupDimension = 'source' | 'medium' | 'campaign' | 'ref' | 'coupon';

export interface SignupSourceRow {
  /** The tag's value; `null` = signed up through no link (or one without this tag). */
  key: string | null;
  signups: number;
  connected: number;
  started: number;
  paid: number;
}

export interface SignupSources {
  by: SignupDimension;
  /** The look-back in days; 0 = every account. */
  days: number;
  rows: SignupSourceRow[];
  total: SignupSourceRow;
}

export interface UserBilling {
  me: BillingMe;
  invoices: Invoice[];
}

export interface BinanceTest {
  ok: boolean;
  deposits: number;
  error: string | null;
}
